import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, RotateCcw, Loader2, Trophy, FileText, Users, Calendar, Award
} from "lucide-react";
import { gradesApi, apiService, classesApi } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== API RESPONSE HELPER ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.results)) return res.results;
  }
  return [];
};

const getErrorDetail = (err: any, fallback: string = ''): string => {
  const data = (err as any)?.response?.data as any;
  return data?.detail || data?.message || err?.message || fallback;
};

// CORRECT localStorage keys for your project
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

// Recuperation school_id depuis auth-storage
const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) {
        return Number(state.user.school_id)
      }
    }
  } catch (e) {
    console.warn('Failed to get school_id from auth-storage:', e)
  }
  return null
}

// Recuperation academic_year_id depuis lybibal-selected-year-id
const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yearId) return Number(yearId)
  } catch (e) {
    console.warn('Failed to get year_id:', e)
  }
  return null
}

// Helper: charge avec school_id, fallback sans school_id si admin et résultat vide
const loadWithFallback = async (
  url: string,
  params: Record<string, any>,
  isPrivileged: boolean
): Promise<any[]> => {
  try {
    const res = await apiService.get(url, { params }).catch(() => ({ data: [] }));
    let data = extractData(res);
    if (data.length === 0 && params.school_id && isPrivileged) {
      const { school_id, ...paramsNoSchool } = params;
      const res2 = await apiService.get(url, { params: paramsNoSchool }).catch(() => ({ data: [] }));
      data = extractData(res2);
      if (data.length > 0) {
        console.warn(`[${url}] Fallback without school_id returned ${data.length} items`);
      }
    }
    return data;
  } catch (e) {
    console.warn(`[${url}] Load error:`, e);
    return [];
  }
};

// ==================== TYPES ====================
interface RankingStudent {
  student_id: number;
  student_name: string;
  matricule: string;
  average: number;
  rank: number;
  appreciation: string;
  subject_grades: Array<{ subject: string; value: number; coefficient: number }>;
}

interface GradeSequence {
  id: number;
  name: string;
  label?: string;
  code?: string;
  start_date: string;
  end_date: string;
  is_open: boolean;
  academic_year_id: number;
  evaluation_period_id?: number;
  max_score?: number;
}

interface ClassOption {
  id: number;
  name: string;
  level_name?: string;
  cycle_name?: string;
  student_count?: number;
}

// ==================== HELPER ====================
const getAppreciationBadgeColor = (value: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) return 'bg-gray-100 text-gray-500';
  if (value >= 16) return 'bg-emerald-100 text-emerald-700';
  if (value >= 14) return 'bg-blue-100 text-blue-700';
  if (value >= 12) return 'bg-cyan-100 text-cyan-700';
  if (value >= 10) return 'bg-gray-100 text-gray-700';
  if (value >= 8) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};

// ==================== MAIN COMPONENT ====================
export default function GradesRankingPage() {
  const { lang } = useLang();
  const { isAdmin, isSuperAdmin, roleName } = useAuthStore();
  const isAdminRole = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';

  // school_id et academic_year_id recuperes automatiquement
  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId)
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId)
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null)

  const t = {
    fr: {
      title: 'Classement des Élèves',
      subtitle: 'Rang et moyennes par classe et séquence',
      selectClass: 'Sélectionner une classe',
      selectSequence: 'Séquence (optionnel)',
      allClasses: 'Toutes les classes',
      allSequences: 'Toutes les séquences',
      matricule: 'Matricule',
      student: 'Élève',
      avg: 'Moy',
      appreciation: 'Appréciation',
      actions: 'Actions',
      generateBulletin: 'Générer le bulletin',
      rankingTitle: 'Classement des Élèves',
      refresh: 'Actualiser',
      loading: 'Chargement...',
      noData: 'Aucune donnée',
      selectFilters: 'Sélectionnez une classe pour voir le classement',
      error: 'Erreur',
      success: 'Succès',
      rank: 'Rang',
      noYearSelected: 'Aucune année scolaire sélectionnée',
      yearRequired: 'Veuillez sélectionner une année scolaire dans le header',
    },
    en: {
      title: 'Student Ranking',
      subtitle: 'Rank and averages by class and sequence',
      selectClass: 'Select a class',
      selectSequence: 'Sequence (optional)',
      allClasses: 'All classes',
      allSequences: 'All sequences',
      matricule: 'ID',
      student: 'Student',
      avg: 'Avg',
      appreciation: 'Appreciation',
      actions: 'Actions',
      generateBulletin: 'Generate report card',
      rankingTitle: 'Student Ranking',
      refresh: 'Refresh',
      loading: 'Loading...',
      noData: 'No data',
      selectFilters: 'Select a class to view ranking',
      error: 'Error',
      success: 'Success',
      rank: 'Rank',
      noYearSelected: 'No academic year selected',
      yearRequired: 'Please select an academic year in the header',
    }
  }[lang];

  // ==================== STATE ====================
  const [rankings, setRankings] = useState<RankingStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedSequence, setSelectedSequence] = useState<string>("");
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [sequences, setSequences] = useState<GradeSequence[]>([]);
  const [selectedClassSchoolId, setSelectedClassSchoolId] = useState<number | null>(null);

  // Refs pour éviter les re-subscriptions
  const schoolIdRef = React.useRef<number | null>(schoolId)
  const yearIdRef = React.useRef<number | null>(selectedAcademicYearId)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = selectedAcademicYearId }, [selectedAcademicYearId])

  // Construire les params de filtrage avec school_id et academic_year_id
  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {}
    if (schoolId) params.school_id = schoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [schoolId, selectedAcademicYearId])

  // Params pour les appels liés à une classe spécifique (utilise le school_id de la classe)
  const getClassApiParams = useCallback(() => {
    const params: Record<string, any> = {}
    const effectiveSchoolId = selectedClassSchoolId || schoolId
    if (effectiveSchoolId) params.school_id = effectiveSchoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [selectedClassSchoolId, schoolId, selectedAcademicYearId])

  // Listen for year-change events
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null

      // Guard: if nothing changed, do nothing to avoid loops
      if (newYearId && newYearId === yearIdRef.current && (newSchoolId === null || newSchoolId === schoolIdRef.current)) {
        return
      }

      if (newYearId && newYearId !== yearIdRef.current) {
        setSelectedAcademicYearId(newYearId)
        const stored = localStorage.getItem(SELECTED_YEAR_ID_KEY)
        if (stored !== String(detail.year_id)) {
          try { localStorage.setItem(SELECTED_YEAR_ID_KEY, detail.year_id.toString()) } catch (err) { /* ignore */ }
        }
        if (detail.label) {
          const storedLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
          try { if (storedLabel !== detail.label) localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detail.label) } catch (err) { /* ignore */ }
          setCurrentAcademicYear({ id: newYearId, name: detail.label })
        }
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolIdRef.current) {
        setSchoolId(newSchoolId)
        try { localStorage.setItem('lybibal-selected-school-id', newSchoolId.toString()); } catch (err) { /* ignore */ }
      }

      // Reset filters when year changes
      setSelectedClass("")
      setSelectedSequence("")
      setRankings([])

      toast.info(lang === 'fr' ? 'Année scolaire changée - rechargement des données' : 'Academic year changed - reloading data')
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId && newYearId !== yearIdRef.current) {
          setSelectedAcademicYearId(newYearId)
          setSelectedClass("")
          setSelectedSequence("")
          setRankings([])
        }
      }
    }
    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [lang])

  // Sync year from storage au montage
  useEffect(() => {
    const yearId = getSelectedYearId()
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
    if (yearId) {
      setSelectedAcademicYearId(yearId)
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel })
    }

    try {
      const storedSchool = localStorage.getItem('lybibal-selected-school-id')
      if (storedSchool) {
        const sid = Number(storedSchool)
        if (!isNaN(sid)) setSchoolId(sid)
      }
    } catch (err) {
      // ignore
    }
  }, [])

  // ==================== DATA LOADING ====================

  const loadFilters = useCallback(async () => {
    if (!selectedAcademicYearId) {
      setClasses([])
      setSequences([])
      return
    }

    try {
      const filterParams = getFilterParams()
      const isPrivileged = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';

      const [classesData, seqsData] = await Promise.all([
        loadWithFallback('/classes', filterParams, isPrivileged),
        loadWithFallback('/grades/sequences', filterParams, isPrivileged),
      ]);

      // Filtrer les séquences par année scolaire (défensive)
      const filteredSeqs = Array.isArray(seqsData) && selectedAcademicYearId
        ? seqsData.filter((s: any) => {
            const matchesYear = (s.academic_year_id === undefined) ? true : Number(s.academic_year_id) === Number(selectedAcademicYearId);
            const matchesSchool = (s.school_id === undefined || !schoolId) ? true : Number(s.school_id) === Number(schoolId);
            return matchesYear && matchesSchool;
          })
        : (Array.isArray(seqsData) ? seqsData : [])

      setClasses(Array.isArray(classesData) ? classesData : []);
      setSequences(filteredSeqs);
    } catch (e) {
      console.error('[GradesRankingPage] loadFilters error:', e);
      setClasses([])
      setSequences([])
    }
  }, [schoolId, selectedAcademicYearId, getFilterParams, isAdmin, isSuperAdmin, roleName]);

  const loadRankings = useCallback(async () => {
    if (!selectedClass || !schoolId || !selectedAcademicYearId) {
      setRankings([]);
      return;
    }
    
    setLoading(true);
    try {
      const filterParams = getFilterParams()
      const params: any = {
        ...filterParams,
        sequence_id: selectedSequence ? parseInt(selectedSequence) : undefined,
      }

      const res = await apiService.get(`/grades/ranking/${selectedClass}`, {
        params
      }).catch(() => ({ data: [] }));
      
      const data = (res as any)?.data ?? res
      setRankings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error(t.error);
      setRankings([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedSequence, schoolId, selectedAcademicYearId, getFilterParams, t.error]);

  useEffect(() => {
    if (schoolId && selectedAcademicYearId) {
      loadFilters();
    }
  }, [loadFilters, schoolId, selectedAcademicYearId]);

  useEffect(() => {
    if (selectedClass && schoolId && selectedAcademicYearId) {
      loadRankings();
    }
  }, [loadRankings]);

  // ==================== HANDLERS ====================

  const handleGenerateBulletin = async (studentId: number) => {
    if (!selectedAcademicYearId) {
      toast.error(lang === 'fr' ? 'Aucune année scolaire sélectionnée' : 'No academic year selected');
      return;
    }
    
    try {
      const filterParams = getFilterParams()
      const params: any = {
        ...filterParams,
        sequence_id: selectedSequence ? parseInt(selectedSequence) : undefined,
      }

      const response = await apiService.get(`/grades/bulletin/${studentId}`, {
        params,
        responseType: 'blob'
      });

      const resData = (response && (response as any).data !== undefined) ? (response as any).data : response;
      const blob = resData instanceof Blob ? resData : new Blob([resData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulletin_${studentId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'fr' ? 'Bulletin généré' : 'Report card generated');
    } catch (err) {
      toast.error(lang === 'fr' ? 'Erreur lors de la génération' : 'Generation error');
    }
  };

  // ==================== RENDER ====================

  // Si pas d'année scolaire sélectionnée, afficher un message
  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {currentAcademicYear.name}
            </span>
            <span className="text-xs text-gray-400">
              school_id: {schoolId || "-"}
            </span>
          </div>
        )}
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Class Select */}
          <div className="relative">
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value)
                setRankings([])
              }}
              className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[180px]"
            >
              <option value="">{t.selectClass}</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <Users className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {/* Sequence Select */}
          <div className="relative">
            <select
              value={selectedSequence}
              onChange={(e) => {
                setSelectedSequence(e.target.value)
                setRankings([])
              }}
              className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[180px]"
            >
              <option value="">{t.selectSequence}</option>
              {sequences.map((s) => (
                <option key={s.id} value={s.id}>{s.label || s.name}</option>
              ))}
            </select>
            <Award className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {/* Refresh Button */}
          <button
            onClick={loadRankings}
            className="p-2.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
            title={t.refresh}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Ranking Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#1e3a8a]" />
            {t.rankingTitle}
          </h3>
          <button
            onClick={loadRankings}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
            title={t.refresh}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16">{t.rank}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t.matricule}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t.student}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t.avg}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t.appreciation}</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">{t.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t.loading}
                    </div>
                  </td>
                </tr>
              ) : !selectedClass ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{t.selectFilters}</p>
                  </td>
                </tr>
              ) : rankings.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{t.noData}</p>
                  </td>
                </tr>
              ) : (
                rankings.map((student) => (
                  <tr key={student.student_id} className={`hover:bg-gray-50 transition-colors ${student.rank <= 3 ? "bg-yellow-50/30" : ""}`}>
                    <td className="px-4 py-3">
                      {student.rank === 1 && <Trophy className="w-5 h-5 text-yellow-500" />}
                      {student.rank === 2 && <span className="text-lg font-bold text-gray-400">2</span>}
                      {student.rank === 3 && <span className="text-lg font-bold text-amber-600">3</span>}
                      {student.rank > 3 && <span className="text-sm text-gray-500 font-medium">{student.rank}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#1e3a8a]">{student.matricule}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.student_name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-sm font-bold ${getAppreciationBadgeColor(student.average)}`}>
                        {student.average.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{student.appreciation}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleGenerateBulletin(student.student_id)}
                        className="p-1.5 hover:bg-blue-50 rounded-lg text-[#1e3a8a] transition-colors"
                        title={t.generateBulletin}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}